import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useCalculos } from "@/hooks/useCalculos";
import { CalculoAluguelResult } from "@/types/calculos";
import { addToHistory, useCalculoPrefill } from "@/hooks/useCalculoHistory";

export const CalculosAluguel = () => {
  const [valorMensal, setValorMensal] = useState("");
  const [diasAtraso, setDiasAtraso] = useState("");
  const [multaPercentual, setMultaPercentual] = useState("10");
  const [jurosDiarios, setJurosDiarios] = useState("0.33");
  const [resultado, setResultado] = useState<CalculoAluguelResult | null>(null);
  
  const { calcularAluguel, loading } = useCalculos();

  useCalculoPrefill("aluguel", (v) => {
    setValorMensal(v.valorMensal ?? "");
    setDiasAtraso(v.diasAtraso ?? "");
    setMultaPercentual(v.multaPercentual ?? "10");
    setJurosDiarios(v.jurosDiarios ?? "0.33");
    setResultado(null);
  });

  const handleCalcular = async () => {
    if (!valorMensal || !diasAtraso) {
      return;
    }

    try {
      const result = await calcularAluguel({
        valorMensal: parseFloat(valorMensal),
        diasAtraso: parseInt(diasAtraso),
        multaPercentual: parseFloat(multaPercentual),
        jurosDiarios: parseFloat(jurosDiarios)
      });
      setResultado(result);
      addToHistory({
        type: "aluguel",
        label: "Aluguel em Atraso",
        values: { valorMensal, diasAtraso, multaPercentual, jurosDiarios },
        summary: `Aluguel ${formatCurrency(parseFloat(valorMensal))} • ${diasAtraso} dias • Total ${formatCurrency(result.total)}`,
      });
    } catch (error) {
      console.error("Erro no cálculo:", error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cálculo de Aluguel em Atraso</CardTitle>
        <p className="text-sm text-muted-foreground">
          Valor devido com multa e juros por atraso
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="valor-mensal">Valor Mensal do Aluguel</Label>
            <Input
              id="valor-mensal"
              type="number"
              placeholder="1200.00"
              value={valorMensal}
              onChange={(e) => setValorMensal(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="dias-atraso">Dias de Atraso</Label>
            <Input
              id="dias-atraso"
              type="number"
              placeholder="30"
              value={diasAtraso}
              onChange={(e) => setDiasAtraso(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="multa">Multa (%)</Label>
            <Input
              id="multa"
              type="number"
              step="0.1"
              placeholder="10"
              value={multaPercentual}
              onChange={(e) => setMultaPercentual(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="juros">Juros Diários (%)</Label>
            <Input
              id="juros"
              type="number"
              step="0.01"
              placeholder="0.33"
              value={jurosDiarios}
              onChange={(e) => setJurosDiarios(e.target.value)}
            />
          </div>
        </div>
        
        <Button 
          onClick={handleCalcular} 
          className="w-full"
          disabled={loading}
        >
          {loading ? "Calculando..." : "Calcular Valor Devido"}
        </Button>
        
        {resultado && (
          <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
            <h3 className="font-semibold mb-2">Valor Total Devido:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Valor do Aluguel:</strong> {formatCurrency(resultado.valorDevido)}</p>
                <p><strong>Multa ({multaPercentual}%):</strong> {formatCurrency(resultado.multa)}</p>
                <p><strong>Juros ({diasAtraso} dias):</strong> {formatCurrency(resultado.juros)}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(resultado.total)}
                </p>
                <p className="text-sm text-muted-foreground">Total a pagar</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};